import { useCallback, useRef, useState } from "react";
import {
  Box,
  Icon,
  Text,
  Image,
  SimpleGrid,
  IconButton,
  Link,
} from "@chakra-ui/react";
import { FiUploadCloud, FiX } from "react-icons/fi";

type ImageValue = File | string;

interface ImageUploadProps {
  maxFiles?: number;
  maxSizeMB?: number;
  accept?: string;
  value?: ImageValue[];
  onChange?: (files: ImageValue[]) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  maxFiles = 10,
  maxSizeMB = 5,
  accept = "image/*",
  value = [],
  onChange,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFiles = useCallback(
    (incoming: FileList | File[]) => {
      const newFiles = Array.from(incoming).filter((file) => {
        if (file.size > maxSizeMB * 1024 * 1024) return false;
        if (!file.type.startsWith("image/")) return false;
        return true;
      });

      const existingUrls = value.filter((v) => typeof v === "string");
      const combined = [...newFiles, ...existingUrls].slice(0, maxFiles);
      onChange?.(combined);
    },
    [value, onChange, maxFiles, maxSizeMB],
  );

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
    e.target.value = "";
  };

  const removeFile = (index: number) => {
    const updated = value.filter((_, i) => i !== index);
    onChange?.(updated);
  };

  return (
    <Box>
      <Text fontWeight="medium" mb={2}>
        Agregar archivos
      </Text>
      <Box
        border="2px dashed"
        borderColor={isDragging ? "brand.400" : "gray.200"}
        borderRadius="lg"
        p={8}
        textAlign="center"
        cursor="pointer"
        bg={isDragging ? "brand.50" : "white"}
        transition="all 0.2s"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
        _hover={{ borderColor: "brand.300" }}
      >
        <Icon as={FiUploadCloud} boxSize={8} color="brand.400" mb={2} />
        <Text color="gray.600">
          Arrastra archivos aquí o{" "}
          <Link color="brand.500" fontWeight="medium">
            selecciona
          </Link>
        </Text>
        <Text fontSize="sm" color="fg.subtle" mt={1}>
          Máximo {maxFiles} archivos (máx. {maxSizeMB}MB cada uno)
        </Text>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={maxFiles > 1}
          hidden
          onChange={handleInputChange}
        />
      </Box>

      {value.length > 0 && (
        <SimpleGrid columns={{ base: 3, md: 4 }} gap={3} mt={4}>
          {value.map((item, index) => {
            const isUrl = typeof item === "string";
            const src = isUrl ? item : URL.createObjectURL(item as File);
            const alt = isUrl ? "Imagen existente" : (item as File).name;
            return (
              <Box
                key={index}
                position="relative"
                borderRadius="md"
                overflow="hidden"
                w="fit-content"
              >
                <Image
                  src={src}
                  alt={alt}
                  boxSize="80px"
                  objectFit="cover"
                  borderRadius="md"
                />
                <IconButton
                  aria-label="Eliminar imagen"
                  size="xs"
                  position="absolute"
                  top={1}
                  right={1}
                  colorPalette="red"
                  borderRadius="full"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(index);
                  }}
                >
                  <FiX />
                </IconButton>
              </Box>
            );
          })}
        </SimpleGrid>
      )}
    </Box>
  );
};

export default ImageUpload;
